// src/components/solution_sections/WorkflowVisualizationSection.tsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';

// Use a named import, as is best practice for non-default exports.
import { Card } from '@dopomogai/ui';
import { AgentCard } from '@dopomogai/ui';
import { loadAgentScenario } from '@dopomogai/data';
import { AgentData_V10 } from '@dopomogai/types';

interface WorkflowRole {
  agentId: string;
  roleOverride?: string;
}
interface WorkflowData {
  sectionTitle?: string;
  introduction?: string;
  agentRoles: WorkflowRole[];
  icon?: string;
}

interface WorkflowVisualizationSectionProps {
  data: WorkflowData;
  headerBgColor?: string;
  onAction: (actionType: string, payload?: any) => void;
  id: string; // The section needs an ID for scroll-spy
}

const AgentCardSkeleton: React.FC = () => (
  <div className="bg-white border-2 border-black rounded-xl p-5 h-full">
    <div className="w-full aspect-[4/3] bg-slate-200 rounded-md mb-4 animate-pulse"></div>
    <div className="h-6 w-3/4 bg-slate-200 rounded mb-2 animate-pulse"></div>
    <div className="h-4 w-full bg-slate-200 rounded mb-6 animate-pulse"></div>
    <div className="h-10 w-full bg-slate-200 rounded animate-pulse"></div>
  </div>
);

export const WorkflowVisualizationSection = React.forwardRef<HTMLDivElement, WorkflowVisualizationSectionProps>(({
  data,
  headerBgColor,
  onAction,
  id,
}, ref) => {
  const { t } = useTranslation('solution_showcase');
  const [loadedAgents, setLoadedAgents] = useState<(AgentData_V10 & { roleOverride?: string })[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAgentData = async () => {
      setIsLoading(true);
      if (!data.agentRoles || data.agentRoles.length === 0) {
        setIsLoading(false);
        return;
      }

      const agentDataPromises = data.agentRoles.map(async (role) => {
        const agentData = await loadAgentScenario(role.agentId);
        if (agentData) {
          return { ...agentData, roleOverride: role.roleOverride };
        }
        return null;
      });

      const results = await Promise.all(agentDataPromises);
      setLoadedAgents(results.filter(agent => agent !== null) as (AgentData_V10 & { roleOverride?: string })[]);
      setIsLoading(false);
    };

    fetchAgentData();
  }, [data.agentRoles]);

  const containerVariants = {
    visible: { transition: { staggerChildren: 0.07 } },
    hidden: {},
  };

  const itemVariants = {
    visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 100, damping: 12 } },
    hidden: { opacity: 0, y: 20 },
  };
  
  // --- THIS IS THE FIX ---
  // We pass header information as props to the <Card> component,
  // and the content goes directly inside it. No more <Card.Header>.
  return (
    <Card
      ref={ref}
      id={id}
      title={data.sectionTitle}
      titleKey={!data.sectionTitle ? 'workflow.defaultTitle' : undefined}
      icon={data.icon}
      headerBgColor={headerBgColor}
    >
      <motion.div
        className="space-y-12"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        variants={containerVariants}
      >
        {data.introduction && (
           <motion.div variants={itemVariants} className="text-center">
            <div className="inline-flex items-center gap-3 bg-indigo-100 text-indigo-700 font-semibold px-4 py-2 rounded-full mb-4">
              <Sparkles size={20} />
              {t('workflow.collaborationHeader')}
            </div>
            <p className="text-xl text-slate-700 max-w-3xl mx-auto leading-relaxed">
              {data.introduction}
            </p>
          </motion.div>
        )}

        <motion.div
          className="grid gap-8"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}
          variants={containerVariants}
        >
          {isLoading
            ? data.agentRoles.map(role => (
                <motion.div key={role.agentId} variants={itemVariants}>
                  <AgentCardSkeleton />
                </motion.div>
              ))
            : loadedAgents.map((agent) => (
              <motion.div key={agent.id} variants={itemVariants}>
                <AgentCard
                  data={{
                    id: agent.id,
                    slug: agent.id,
                    name: agent.roleOverride || agent.name,
                    tagline: agent.tagline,
                    visual: agent.heroVisual,
                    creatorName: agent.creatorName,
                    version: agent.version,
                   // badges: agent.heroBadges,
                  }}
                  variant="compact"
                  onAction={onAction}
                  showImpactSnippet={false}
                  maxBadges={3}
                  className="h-full"
                />
              </motion.div>
            ))}
        </motion.div>
      </motion.div>
    </Card>
  );
});

